import { Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { Inline, vars } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { css, mediaQueries } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import Media from 'react-media';
import { getStepGradient } from '../../../utils/stepIndicator';

const wrapperStyles = css`
  background-color: #fff;
  font-size: 0.875rem;
  overflow: hidden;
`;

const mobileStepStatusStyles = css`
  font-weight: 700;
`;

const gutterStyles = (currentStep, numberOfSteps) => css`
  --direction: right;

  background: linear-gradient(to var(--direction), ${getStepGradient(currentStep, numberOfSteps)});
  height: 4px;
  position: relative;
  width: 100%;

  [dir='rtl'] & {
    --direction: left;
  }

  &::before {
    background-color: ${vars.colors.decoration.dark};
    content: '';
    height: 100%;
    inset-inline-start: -100%;
    position: absolute;
    top: 0;
    width: 100%;
  }

  &::after {
    background-color: ${vars.colors.decoration.light};
    content: '';
    height: 100%;
    inset-inline-end: -100%;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

const verticalSpacing = css`
  padding: 12px 0;

  @media ${mediaQueries.large.up} {
    padding: 8px 0;
  }
`;

const getStepStyles = (percentageWidth) => css`
  padding-inline-end: 40px;
  width: ${percentageWidth}%;
`;

const StepIndicatorWrapper = ({ children, currentStep }) => {
  const { t } = useTranslation();
  const initialSteps = [];
  const isInitiallyVisited = true;
  const steps = Children.toArray(children).filter(Boolean);
  const currentStepIndex = steps.findIndex((step) => step.props.stepKey === currentStep) + 1;

  const [transformedSteps] = steps.reduce(
    (acc, step, index) => {
      const isCurrent = step.props.stepKey === currentStep;
      const isVisited = acc[1] && !isCurrent;
      return [
        [
          ...acc[0],
          cloneElement(step, {
            className: getStepStyles(100 / steps.length),
            id: index + 1,
            isCurrent,
            isVisited,
          }),
        ],
        isVisited,
      ];
    },
    [initialSteps, isInitiallyVisited],
  );

  return (
    <div className={wrapperStyles} data-testid="step-indicator">
      <ContentWrapper>
        <Media query={mediaQueries.large.up}>
          {(matches) => (
            <Inline
              aria-label={t('ScreenReader.StepIndicator.Step.Complete.Text')}
              as={matches ? 'ul' : 'div'}
              className={verticalSpacing}
              noWrap
              spacing={0}
            >
              {matches ? (
                transformedSteps
              ) : (
                <span className={mobileStepStatusStyles} data-testid="current-step">
                  {t('StepIndicator.MobileViewports.Label', currentStepIndex, steps.length)}
                </span>
              )}
            </Inline>
          )}
        </Media>
        <div className={gutterStyles(currentStepIndex - 1, steps.length)} />
      </ContentWrapper>
    </div>
  );
};

StepIndicatorWrapper.propTypes = {
  children: PropTypes.node,
  currentStep: PropTypes.string,
};

export default StepIndicatorWrapper;
